import type { TriageRunSummary, TriageStats } from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { triageList, triageStats } from "../triage-api";

export const REASON_LABELS: Record<string, string> = {
  gate_failed: "Failed the quality gate",
  l1_autonomy: "Draft ready (L1: a person promotes)",
  l0_autonomy: "Research only (L0)",
  board_hold: "Board on hold",
  revision_conflicts: "Card kept changing",
  lock_timeout: "Dedupe lock timed out",
  ambiguous_duplicate: "Ambiguous duplicate",
  unknown_identifier: "Unknown identifier",
  destructive_or_irreversible: "Destructive or irreversible",
  permission_or_admin_work: "Permission or admin work",
  credential_or_regulated_data: "Credential or regulated data",
  no_wiki_coverage: "No wiki coverage",
  priority_one: "Priority 1",
};

export function reasonLabel(run: {
  queueReason: string | null;
  stopReason: string | null;
}): string {
  const key = run.queueReason ?? run.stopReason ?? "";
  return REASON_LABELS[key] ?? key;
}

export function age(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

export function StaffTriageQueue() {
  const [items, setItems] = useState<TriageRunSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [stats, setStats] = useState<TriageStats | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [showReviewed, setShowReviewed] = useState(false);

  async function load(next?: string) {
    setState("loading");
    try {
      const page = await triageList({
        cursor: next,
        limit: "50",
        reviewed: showReviewed ? "true" : "false",
      });
      setItems((current) => (next ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
    triageStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, [showReviewed]);

  return (
    <section aria-labelledby="triage-heading">
      <h2 id="triage-heading">Triage review</h2>
      {stats ? (
        <p>
          Waiting for a person: {stats.queuedTotal}
          {stats.oldestQueuedAgeMs !== null ? `, oldest ${age(stats.oldestQueuedAgeMs)}` : ""}.{" "}
          {stats.byCategory
            .filter((c) => c.promoted > 0)
            .map(
              (c) =>
                `${c.category}: edit rate ${Math.round((c.editRate ?? 0) * 100)}% over ${c.promoted}`,
            )
            .join("; ")}
        </p>
      ) : null}
      <p>
        <label>
          <input
            type="checkbox"
            checked={showReviewed}
            onChange={(e) => setShowReviewed(e.target.checked)}
          />{" "}
          Show reviewed runs
        </label>
      </p>
      {state === "error" ? <p role="alert">The triage queue could not be loaded.</p> : null}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: "left" }}>
                Request
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Why it is here
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Shape
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Confidence
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Age
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Review
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((run) => (
              <tr key={run.id} style={{ borderTop: "1px solid #ddd" }}>
                <td>
                  <a href={`/staff/triage/${run.id}`}>{run.publicId}</a> {run.subject}
                  <br />
                  <span style={{ fontSize: "0.85rem", color: "#555" }}>{run.category}</span>
                </td>
                <td>{reasonLabel(run)}</td>
                <td>{run.shape.replaceAll("_", " ")}</td>
                <td>{run.confidence === null ? "" : `${Math.round(run.confidence * 100)}%`}</td>
                <td>{age(run.ageMs)}</td>
                <td>
                  {run.reviewDecision
                    ? `${run.reviewDecision} by ${run.reviewerDisplayName ?? "staff"}`
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor ? (
        <button type="button" onClick={() => void load(cursor)} disabled={state === "loading"}>
          Load more
        </button>
      ) : null}
      <p role="status" aria-live="polite">
        {state === "loading" ? "Loading…" : ""}
      </p>
    </section>
  );
}
